import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import * as storageService from '../services/storageService';
import { Hospital, PatientStatus, STATUS_CONFIG, SUB_STATUS_CONFIG } from '../types';
import { Search, Shield, Lock, ArrowRight, HeartPulse, Building } from 'lucide-react';
import VisitorPinGate from '../components/VisitorPinGate';

const HospitalHome: React.FC = () => {
    const { handle } = useParams<{ handle: string }>();
    const navigate = useNavigate();
    const [hospital, setHospital] = useState<Hospital | null>(null);
    const [loading, setLoading] = useState(true);
    const [code, setCode] = useState('');
    const [patients, setPatients] = useState<any[]>([]);
    const [rooms, setRooms] = useState<any[]>([]);

    // Visibility gate state
    const [visibilityMode, setVisibilityMode] = useState<'public' | 'pin' | 'private' | null>(null);
    const [visitorVerified, setVisitorVerified] = useState(false);
    const [visibilityMeta, setVisibilityMeta] = useState<any>(null);

    // Check visibility first (lightweight, no auth needed)
    useEffect(() => {
        if (!handle) return;
        fetch(`/api/hospitals/visibility/${handle}`)
            .then(r => r.json())
            .then(data => {
                setVisibilityMode(data.mode || 'pin');
                setVisibilityMeta(data);

                // Check if visitor already has a valid token
                if (data.mode === 'public') {
                    setVisitorVerified(true);
                } else if (data.mode === 'pin') {
                    const token = sessionStorage.getItem(`visitor_token_${handle}`);
                    if (token) {
                        try {
                            const payloadBase64 = token.split('.')[1];
                            const parsed = JSON.parse(atob(payloadBase64.replace(/-/g, '+').replace(/_/g, '/')));
                            if (parsed.exp > Math.floor(Date.now() / 1000)) {
                                setVisitorVerified(true);
                            } else {
                                sessionStorage.removeItem(`visitor_token_${handle}`);
                            }
                        } catch {
                            sessionStorage.removeItem(`visitor_token_${handle}`);
                        }
                    }
                }
                // 'private' stays unverified
            })
            .catch(() => {
                setVisibilityMode('public'); // Fallback: allow access if endpoint fails
                setVisitorVerified(true);
            });
    }, [handle]);

    useEffect(() => {
        // Only load patient data after visitor is verified
        if (!visitorVerified) return;

        let unsubP: (() => void) | null = null;
        let unsubR: (() => void) | null = null;

        const load = async () => {
            if (handle) {
                const found = await storageService.getHospitalByHandle(handle);
                setHospital(found || null);

                if (found) {
                    // Initial load
                    const [p, r] = await Promise.all([
                        storageService.getPatientsByHospital(found.id),
                        storageService.getRoomsByHospital(found.id)
                    ]);
                    setPatients(p);
                    setRooms(r);

                    // Subscribe to real-time updates
                    unsubP = storageService.subscribeToPatients(found.id, (data) => setPatients(data));
                    unsubR = storageService.subscribeToRooms(found.id, (data) => setRooms(data));
                }
            }
            setLoading(false);
        };
        load();

        return () => {
            if (unsubP) unsubP();
            if (unsubR) unsubR();
        };
    }, [handle, visitorVerified]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        if (code.trim()) {
            navigate('/family', { state: { prefillCode: code.toUpperCase() } });
        }
    };

    // ── Visibility Gate: block content if not verified ──
    if (visibilityMode && !visitorVerified && visibilityMeta) {
        return (
            <VisitorPinGate
                handle={handle || ''}
                hospitalName={visibilityMeta.hospitalName}
                logoUrl={visibilityMeta.logoUrl}
                primaryColor={visibilityMeta.primaryColor}
                secondaryColor={visibilityMeta.secondaryColor}
                backgroundColor={visibilityMeta.backgroundColor}
                mode={visibilityMode as 'pin' | 'private'}
                onVerified={(token) => setVisitorVerified(true)}
            />
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 relative pt-32 pb-10 px-4 md:px-8 max-w-7xl mx-auto space-y-12">
                <div className="text-center max-w-3xl mx-auto mb-20 animate-pulse">
                     <div className="h-8 md:h-12 bg-slate-200 rounded-2xl w-3/4 mx-auto mb-6" />
                     <div className="h-4 md:h-6 bg-slate-200 rounded-xl w-1/2 mx-auto mb-10" />
                     <div className="h-14 md:h-[60px] bg-slate-200 rounded-full w-full shadow-sm" />
                </div>
                <div className="bg-white rounded-[2rem] overflow-hidden shadow-sm border border-slate-100 animate-pulse">
                     <div className="h-16 bg-slate-100 border-b border-slate-200" />
                     <div className="divide-y divide-slate-50">
                          {[1, 2, 3, 4].map(i => (
                              <div key={i} className="h-20 bg-white" />
                          ))}
                     </div>
                </div>
            </div>
        );
    }

    if (!hospital) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
                <div className="bg-slate-200 p-4 rounded-full mb-4">
                    <Building className="w-8 h-8 text-slate-400" />
                </div>
                <h1 className="text-2xl font-bold text-slate-800 mb-2">Hospital no encontrado</h1>
                <p className="text-slate-500 mb-6">El enlace que ingresaste no corresponde a ningún hospital registrado.</p>
                <Link to="/" className="text-indigo-600 font-bold hover:underline">Ir a Hospital Status Global</Link>
            </div>
        );
    }

    const primaryColor = hospital.branding.primaryColor || '#5B65DC';
    const secondaryColor = hospital.branding.secondaryColor || '#122056';
    const bgColor = hospital.branding.backgroundColor || '#f2f2f7';

    // Filter active patients for the board
    const activePatients = patients.filter(p => {
        if (p.currentStatus !== PatientStatus.EXIT) return true;
        // Keep EXIT patients for 2 hours to show they left
        if (p.dischargeTime) {
            const discharge = new Date(p.dischargeTime).getTime();
            const now = new Date().getTime();
            return (now - discharge) < 2 * 60 * 60 * 1000;
        }
        return false;
    }).sort((a, b) => {
        // Sort: Active first, then by time
        if (a.currentStatus === PatientStatus.EXIT && b.currentStatus !== PatientStatus.EXIT) return 1;
        if (a.currentStatus !== PatientStatus.EXIT && b.currentStatus === PatientStatus.EXIT) return -1;
        return new Date(b.admissionTime).getTime() - new Date(a.admissionTime).getTime();
    });

    const getStatusDisplay = (patient: any) => {
        if (patient.currentStatus === PatientStatus.SURGERY && patient.currentSubStatus) {
            return SUB_STATUS_CONFIG[patient.currentSubStatus as keyof typeof SUB_STATUS_CONFIG]?.label || patient.currentSubStatus;
        }
        return STATUS_CONFIG[patient.currentStatus as PatientStatus]?.label || patient.currentStatus;
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case PatientStatus.WAITING: return 'bg-blue-500';
            case PatientStatus.PREPARATION: return 'bg-blue-500';
            case PatientStatus.ANESTHESIA: return 'bg-amber-500';
            case PatientStatus.SURGERY: return 'bg-indigo-600';
            case PatientStatus.RECOVERY: return 'bg-emerald-500';
            case PatientStatus.EXIT: return 'bg-slate-700';
            default: return 'bg-slate-500';
        }
    };

    const getInitials = (name: string) => {
        if (!name || name === 'Paciente Anónimo') return '';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 3);
    };

    return (
        <div className="min-h-screen font-sans selection:text-white" style={{ backgroundColor: bgColor }}>
            <style>{`
                ::selection {
                    background-color: ${primaryColor};
                }
                .airline-row {
                    transition: all 0.3s ease;
                }
                .airline-row:hover {
                    transform: scale(1.01);
                    box-shadow: 0 10px 20px -10px rgba(0,0,0,0.1);
                }
            `}</style>

            {/* Navbar */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-slate-200/50 shadow-sm transition-all duration-300">
                <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-4 group">
                        {hospital.branding.logoDarkUrl && window.matchMedia('(prefers-color-scheme: dark)').matches ? (
                            <img src={hospital.branding.logoDarkUrl} alt="Logo" className="h-8 md:h-12 w-auto object-contain filter drop-shadow-sm group-hover:scale-105 transition-transform" />
                        ) : hospital.branding.logoUrl ? (
                            <img src={hospital.branding.logoUrl} alt="Logo" className="h-8 md:h-12 w-auto object-contain filter drop-shadow-sm group-hover:scale-105 transition-transform" />
                        ) : (
                            <div className="p-2 rounded-xl md:p-2.5 md:rounded-2xl bg-gradient-to-br from-slate-100 to-white shadow-sm ring-1 ring-slate-200 group-hover:scale-105 transition-transform">
                                <Building className="w-5 h-5 md:w-6 md:h-6" style={{ color: primaryColor }} />
                            </div>
                        )}
                        <span className="text-lg md:text-2xl font-bold font-heading tracking-tight truncate max-w-[150px] md:max-w-none" style={{ color: secondaryColor }}>
                            {hospital.name}
                        </span>
                    </div>

                    <div className="flex items-center gap-2 md:gap-4">
                        <Link
                            to={`/${handle}/staff`}
                            className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-slate-800 transition-all px-4 py-2.5 rounded-full hover:bg-slate-100 hover:shadow-sm"
                        >
                            <Lock className="w-4 h-4" />
                            Acceso Staff
                        </Link>
                    </div>
                </div>
            </nav>

            <div className="pt-32 pb-10 px-4 md:px-8 max-w-7xl mx-auto space-y-12 relative z-10">

                {/* Hero / Search */}
                <div className="text-center max-w-3xl mx-auto mb-20">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/80 backdrop-blur-md border border-slate-200 shadow-sm mb-6 animate-fade-in hover:shadow-md transition-shadow">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: primaryColor }}></span>
                            <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: primaryColor }}></span>
                        </span>
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest font-heading">Recepción Digital</span>
                    </div>
                    <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tighter font-heading animate-fade-in" style={{ color: secondaryColor, animationDelay: '0.1s' }}>
                        Tablero de Cirugías
                    </h1>
                    <p className="text-slate-500 text-xl mb-10 font-medium animate-fade-in" style={{ animationDelay: '0.2s' }}>
                        Estado en tiempo real de los procedimientos quirúrgicos.
                    </p>

                    <div className="bg-white/90 backdrop-blur-xl p-2 md:p-2.5 rounded-full shadow-[0_20px_40px_-15px_rgba(0,0,0,0.1)] border border-white flex items-center relative z-20 transition-all focus-within:scale-[1.02] focus-within:ring-2 duration-300 animate-fade-in ring-1 ring-slate-200/50" style={{ animationDelay: '0.3s' }}>
                        <div className="pl-6 pr-2 py-2 flex-1 text-left w-full flex items-center">
                            <Search className="w-5 h-5 text-slate-400 mr-3 hidden md:block" />
                            <div className="w-full">
                                <label htmlFor="code-input" className="block text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5 font-heading">
                                    Localizar Paciente
                                </label>
                                <input
                                    id="code-input"
                                    type="text"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                                    placeholder="Ej. MT001"
                                    className="w-full text-base md:text-xl font-bold placeholder:text-slate-300 focus:outline-none bg-transparent font-mono uppercase tracking-wide"
                                    style={{ color: secondaryColor }}
                                />
                            </div>
                        </div>
                        <button
                            onClick={handleSearch}
                            className="text-white h-12 w-12 md:h-[60px] md:w-auto md:px-8 rounded-full transition-all duration-300 transform active:scale-95 shadow-lg flex items-center justify-center gap-2 hover:brightness-110 group flex-shrink-0"
                            style={{ backgroundColor: primaryColor, boxShadow: `0 8px 20px -5px ${primaryColor}60` }}
                        >
                            <Search className="w-5 h-5 group-hover:scale-110 transition-transform md:hidden" />
                            <span className="font-bold font-heading text-lg hidden md:block">Buscar</span>
                        </button>
                    </div>
                </div>

                {/* Airline Board */}
                <div className="bg-slate-900 rounded-[2rem] overflow-hidden shadow-2xl ring-1 ring-slate-900/5 animate-fade-in" style={{ animationDelay: '0.4s' }}>
                    {/* Header */}
                    <div className="px-8 py-5 flex justify-between items-center border-b border-white/10" style={{ backgroundColor: secondaryColor }}>
                        <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-red-400 animate-pulse shadow-[0_0_10px_rgba(248,113,113,0.5)]"></div>
                            <span className="text-white/80 font-heading text-xs font-bold uppercase tracking-widest">En Vivo • {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <div className="text-white/50 text-xs font-heading font-bold uppercase tracking-widest hidden md:block">
                            Actualización Automática
                        </div>
                    </div>

                    {/* Table Header (Hidden on Mobile) */}
                    <div className="hidden md:grid grid-cols-12 gap-2 px-8 py-4 bg-slate-800/80 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400 border-b border-white/5 font-heading">
                        <div className="col-span-3">Paciente ID</div>
                        <div className="col-span-4">Quirófano</div>
                        <div className="col-span-3 text-left">Estatus</div>
                        <div className="col-span-2 text-right">Ingreso</div>
                    </div>

                    {/* Rows */}
                    <div className="divide-y divide-white/5 bg-slate-900/50 backdrop-blur-md">
                        {activePatients.length > 0 ? (
                            activePatients.map((patient) => {
                                const roomName = rooms.find(r => r.id === patient.room)?.name || 'Asignando...';
                                const statusColor = getStatusColor(patient.currentStatus);
                                const isExit = patient.currentStatus === PatientStatus.EXIT;
                                const isSurgery = patient.currentStatus === PatientStatus.SURGERY;

                                return (
                                    <div key={patient.id} className="flex flex-col md:grid md:grid-cols-12 gap-3 md:gap-2 px-6 py-5 md:px-8 md:py-6 md:items-center airline-row hover:bg-white/5 transition-colors group relative border-b border-white/5 md:border-none">

                                        {/* Mobile Header: ID and Status */}
                                        <div className="flex justify-between items-start md:col-span-3 md:block">
                                            <div className="font-mono text-xl md:text-2xl font-bold text-white tracking-wider group-hover:text-med-light transition-colors">
                                                {patient.id}
                                            </div>
                                            {/* Status on Mobile */}
                                            <div className="md:hidden">
                                                <div
                                                    className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold tracking-wide font-heading border border-white/10 ${isExit ? 'bg-slate-800 text-slate-400' : 'text-white'}`}
                                                    style={isSurgery ? { backgroundColor: primaryColor, boxShadow: `0 0 15px ${primaryColor}40` } : !isExit ? { backgroundColor: statusColor.replace('bg-', '') } : {}}
                                                >
                                                    <span>
                                                        {isExit && <ArrowRight className="w-3 h-3 mr-1 inline" />}
                                                        {getStatusDisplay(patient)}
                                                    </span>
                                                    {isSurgery && <span className="ml-1.5 animate-pulse w-1.5 h-1.5 rounded-full bg-white flex-shrink-0"></span>}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="md:col-span-4 flex items-center gap-2 text-slate-400 md:text-slate-300">
                                            <span className="text-[10px] uppercase tracking-widest font-bold md:hidden">Quirófano:</span>
                                            <div className="text-sm md:text-lg font-medium truncate font-heading group-hover:text-white transition-colors">
                                                {roomName}
                                            </div>
                                        </div>

                                        {/* Status on Desktop */}
                                        <div className="hidden md:block md:col-span-3 text-left">
                                            <div
                                                className={`inline-flex items-center px-4 py-1.5 rounded-xl text-sm font-bold tracking-wide font-heading border border-white/10 ${isExit ? 'bg-slate-800 text-slate-400' : 'text-white'}`}
                                                style={isSurgery ? { backgroundColor: primaryColor, boxShadow: `0 0 15px ${primaryColor}40` } : !isExit ? { backgroundColor: statusColor.replace('bg-', '') } : {}}
                                            >
                                                <span className="truncate max-w-none">
                                                    {isExit && <ArrowRight className="w-3.5 h-3.5 mr-1 inline" />}
                                                    {getStatusDisplay(patient)}
                                                </span>
                                                {isSurgery && <span className="ml-2 animate-pulse w-1.5 h-1.5 rounded-full bg-white flex-shrink-0"></span>}
                                            </div>
                                        </div>

                                        {/* Admission Time */}
                                        <div className="md:col-span-2 flex items-center gap-2 text-slate-500 md:text-slate-400 md:justify-end">
                                            <span className="text-[10px] uppercase tracking-widest font-bold md:hidden">Ingreso:</span>
                                            <div className="font-mono text-xs md:text-lg font-medium group-hover:text-slate-300 transition-colors">
                                                {new Date(patient.admissionTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="py-24 text-center">
                                <div className="inline-block p-5 rounded-3xl bg-white/5 mb-5 ring-1 ring-white/10">
                                    <HeartPulse className="w-10 h-10 text-slate-600 animate-pulse" />
                                </div>
                                <p className="text-slate-400 font-heading text-lg font-medium">No hay cirugías activas en este momento.</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="text-center opacity-50 space-y-4">
                    <p className="text-xs text-slate-400 max-w-md mx-auto">
                        * Los datos mostrados son actualizados en tiempo real por el equipo médico del hospital.
                        Para más detalles, consulte en recepción.
                    </p>
                    <div className="pt-8 border-t border-slate-200">
                        <p className="text-[10px] font-bold tracking-widest uppercase text-slate-300">
                            Desarrollado por ⚕️ ViMaware 2026
                        </p>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default HospitalHome;
